import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn, signInWithRedirect, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import logger from '../services/logger';
import OAuthButton from './OAuthButton';
import Logo from './Logo';

function Login() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      navigate('/plans');
    }
  }, [user, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      setError('');
      setShowConfirmation(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const signInWith = async (provider) => {
    try {
      setLoading(true);
      await signInWithRedirect({ provider });
    } catch (error) {
      console.error('OAuth error:', error);
      if (error.name === 'UserAlreadyAuthenticatedException') {
        setError('You are already signed in. Please logout first or refresh the page.');
      } else {
        setError(`${provider} login failed: ${error.message}`);
      }
      setLoading(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    
    console.log('🔍 handleLogin started:', {
      email: formData.email,
      hasUser: !!user,
      userEmail: user?.attributes?.email
    });
    
    try {
      const result = await signIn({
        username: formData.email,
        password: formData.password
      });
      
      console.log('🔍 signIn successful:', {
        userId: result?.userId,
        isSignedIn: result?.isSignedIn
      });
      
      // Log user login after successful sign in
      await logger.logUserLogin(result);
      
      // Wait for auth state to update, then navigate
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate('/plans');
    } catch (error) {
      console.log('🔍 handleLogin error:', {
        name: error.name,
        message: error.message,
        code: error.code
      });
      
      // Check if user needs email confirmation
      if (error.message.includes('User is not confirmed') || error.name === 'UserNotConfirmedException') {
        setShowConfirmation(true);
        setError('Please check your email and enter the confirmation code.');
      } else if (error.name === 'UserAlreadyAuthenticatedException') {
        console.log('🔍 UserAlreadyAuthenticatedException - forcing logout and retry');
        try {
          // Force logout to clear stale state
          await logout();
          // Clear any localStorage tokens
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('CognitoIdentityServiceProvider')) {
              localStorage.removeItem(key);
            }
          });
          setError('Cleared stale session. Please try logging in again.');
        } catch (logoutError) {
          console.error('Force logout failed:', logoutError);
          setError('Authentication state conflict. Please refresh the page and try again.');
        }
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmation = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await confirmSignUp({
        username: formData.email,
        confirmationCode
      });
      
      // Automatically sign in after confirmation
      const result = await signIn({
        username: formData.email,
        password: formData.password
      });
      
      // Log user login after successful confirmation and sign in
      await logger.logUserLogin(result);
      
      navigate('/plans');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
        <div>
          <Link to="/" className="no-underline">
            <Logo height="32px" />
          </Link>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/" className="text-gray-700 hover:text-blue-500 no-underline">Home</Link>
          <Link to="/signup" className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold no-underline">
            Sign Up
          </Link>
          {user && (
            <button 
              onClick={handleLogout}
              className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer"
            >
              Logout
            </button>
          )}
        </div>
      </nav>

      <div className="flex justify-center items-center min-h-screen pt-20 px-8 bg-gray-50">
        <div className="bg-white p-12 rounded-2xl shadow-xl w-full max-w-md text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
          <p className="text-gray-600 mb-8">Sign in to your account</p>

          {error && (
            <div className="bg-red-100 text-red-600 p-4 rounded-xl mb-4 text-center">
              {error}
            </div>
          )}

          <div className="mb-8 space-y-3">
            <OAuthButton 
              provider="google"
              onClick={() => signInWith('Google')}
              className={loading ? 'opacity-70 cursor-not-allowed' : ''}
            />
            <OAuthButton 
              provider="facebook"
              onClick={() => signInWith('Facebook')}
              className={loading ? 'opacity-70 cursor-not-allowed' : ''}
            />
            <OAuthButton 
              provider="apple"
              onClick={() => signInWith('SignInWithApple')}
              className={loading ? 'opacity-70 cursor-not-allowed' : ''}
            />
            <OAuthButton 
              provider="microsoft"
              onClick={() => signInWith('LoginWithAmazon')}
              className={loading ? 'opacity-70 cursor-not-allowed' : ''}
            />
          </div>

          <div className="my-8 text-gray-600 relative">
            <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-200"></div>
            <span className="bg-white px-4">or</span>
          </div>

          {!showConfirmation ? (
            <form onSubmit={handleLogin} className="flex flex-col gap-4">
              <input 
                type="email" 
                name="email"
                placeholder="Email Address" 
                value={formData.email}
                onChange={handleInputChange}
                required 
                className="p-4 border-2 border-gray-200 rounded-xl text-base"
              />
              <input 
                type="password" 
                name="password"
                placeholder="Password" 
                value={formData.password}
                onChange={handleInputChange}
                required 
                className="p-4 border-2 border-gray-200 rounded-xl text-base"
              />
              <button 
                type="submit" 
                disabled={loading}
                className={`btn-primary text-lg ${
                  loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleConfirmation} className="flex flex-col gap-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Check Your Email</h2>
              <p className="text-gray-600 mb-4">
                We sent a confirmation code to {formData.email}
              </p>
              <input 
                type="text" 
                placeholder="Enter confirmation code" 
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                required 
                className="p-4 border-2 border-gray-200 rounded-xl text-base text-center"
              />
              <button 
                type="submit" 
                disabled={loading}
                className={`btn-primary text-lg ${
                  loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {loading ? 'Confirming...' : 'Confirm Account'}
              </button>
              <div className="flex flex-col gap-2 mt-4">
                <button 
                  type="button" 
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await resendSignUpCode({ username: formData.email });
                      setError('Confirmation code resent to your email.');
                    } catch (error) {
                      setError('Failed to resend code: ' + error.message);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer text-sm"
                >
                  Resend confirmation code
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowConfirmation(false)}
                  className="text-gray-600 hover:text-gray-800 bg-transparent border-none cursor-pointer text-sm"
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          )}

          {!showConfirmation && (
            <>
              <p className="mt-6 text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="text-blue-500 hover:text-blue-600 font-semibold no-underline">
                  Sign up here
                </Link>
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Signed up but didn't confirm your email? Try logging in - we'll help you confirm it.
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Login;